import type { SearchAllRow } from "@/lib/search";

/**
 * One results pool, three kinds of row (日本-498) — everything about drawing one.
 *
 * **The ORDERING left this file** (日本-513). It used to be here, and the reasoning for that is
 * still sound as far as it went: `/lookup` is pinned by the golden fixtures and answers a
 * `Definition`, which a grammar entry is not, so merging into it would move a fixture. What changed
 * is the number of callers. 検索 and Read's dock were two browsers running one TS rule; the MCP's
 * `search` tool is neither, and a second implementation of exact-first ordering in a second
 * language is the drift `engine/grammar_keys.py` and `engine/markup.py` exist to prevent.
 *
 * So `POST /api/search/all` — a **third** route, leaving both the others untouched and the fixtures
 * still — composes the two and returns the order, and `nihongonator/engine/ranking.py` holds the
 * rule that was `mergeResults`, comments and measurements included. What stays here is what is
 * genuinely the client's: the gutter word, the row key, the term a row is keyed on, and the
 * citation line.
 *
 * The `name` kind is declared and not yet produced. It is here because the gutter and the ordering
 * both have to make room for it — Alex flagged name results as coming — and a kind that arrives
 * later without a slot is a second pass over every row component.
 */

export type ResultKind = "vocab" | "grammar" | "name";

/** The gutter word. **Japanese chrome**: these are labels, never keys — see `CHROME.md`. */
export const KIND_LABEL: Record<ResultKind, string> = {
  // 語彙 rather than 辞書 or 単語 (Alex, 27 Aug 2026). 辞書 is what the top bar already calls the
  // whole surface and DoJG is a dictionary too; 単語 is the rail's word for the Cards destination,
  // and one word naming two things on one screen is the trap the chrome rules keep flagging.
  vocab: "語彙",
  grammar: "文法",
  name: "名前",
};

/**
 * A row of the merged pool, as the server hands it over.
 *
 * Re-exported rather than redefined so every component keeps importing its row type from the module
 * that draws rows, while the shape itself has exactly one definition — in `lib/search.ts`, beside
 * the request that produces it.
 */
export type ResultRow = SearchAllRow;

/**
 * How many of each kind, for the count line under the search field.
 *
 * Reported separately rather than as one total: `語彙13件 · 文法3件` says something a bare `16件`
 * does not, which is whether the grammar dictionary answered at all.
 */
export function countByKind(rows: readonly ResultRow[]): Record<ResultKind, number> {
  const counts: Record<ResultKind, number> = { vocab: 0, grammar: 0, name: 0 };
  for (const row of rows) counts[row.kind] += 1;
  return counts;
}

/**
 * The term a row is keyed on for the collection question.
 *
 * `card-signal` and `card-entries` are asked about strings, and a grammar row's string is its
 * **concept** — which is also what a grammar card's `Japanese` field holds, so the two agree by
 * construction rather than by convention.
 */
export function rowTerm(row: ResultRow): string | null {
  return row.kind === "vocab" ? row.entry.term : row.entry.concept;
}

/** A stable React key. Two rows can share a term — 日本-290 — so the index is part of it. */
export function rowKey(row: ResultRow, index: number): string {
  return row.kind === "vocab"
    ? `vocab-${row.entry.term}-${row.entry.seq}-${index}`
    : `grammar-${row.entry.id}-${index}`;
}

/**
 * The volumes' own Japanese (Alex, 30 Aug 2026).
 *
 * **This reverses what `citation` used to say** — that the volume stays English because it names a
 * real edition. The reasoning was right and the conclusion was not: the real edition has a Japanese
 * name. 『日本語基本文法辞典』, 『日本語文法辞典 中級編』, 『日本語文法辞典 上級編』. So this is
 * the edition's own name rather than a rendering of the English one.
 *
 * Here rather than in `surfaces/grammar/` because BOTH surfaces draw it — Read's dock and 文法 —
 * and two copies is the drift this repo keeps writing rules against. Falls through to the source
 * string for a volume it does not know, on `grammarPos.ts`'s rule.
 */
export const VOLUME_LABEL: Record<string, string> = {
  Basic: "基本",
  Intermediate: "中級",
  Advanced: "上級",
};

/** The citation line: `基本 · p.403`. */
export function citation(entry: { volume: string; page: string }): string {
  return `${VOLUME_LABEL[entry.volume] ?? entry.volume} · p.${entry.page}`;
}

/**
 * The related-expression chips, out of `【Related Expression: か2; かい】`.
 *
 * The field is raw source text, so this is deliberately forgiving: it takes what is inside the
 * brackets, splits on the separators the corpus actually uses, and answers nothing at all when it
 * recognises none of it. An unparsed `related` renders as no chips rather than as one chip holding
 * the whole string with its 【】 still on.
 *
 * The trailing digit on `か2` is the source's own disambiguator for a concept written `か (2)`. It
 * is kept: it is what the reader would search for to find that entry.
 */
export function relatedChips(related: string | null): string[] {
  if (!related) return [];
  const inside = /[【\[]([^】\]]*)[】\]]/.exec(related);
  const body = (inside?.[1] ?? related).replace(/^[^:：]*[:：]/, "");
  return body
    .split(/[;；、,]/)
    .map((part) => part.trim())
    .filter((part) => part.length > 0 && part !== "None");
}

/**
 * The count line both surfaces draw — `語彙13件 · 文法3件` (日本-498).
 *
 * **Here rather than in each surface**, because Read's dock and 検索 draw the same list and a
 * second copy of this would be a second place the two could disagree about whether the grammar
 * dictionary answered. It is also the whole reason a kind that produced nothing is *omitted*
 * rather than drawn as a zero: `文法0件` on every ordinary vocabulary search is noise, while
 * 語彙0件 beside 文法2件 is the finding — a query the dictionary could not answer and the grammar
 * book could.
 *
 * 語彙 rather than 単語, which is already the rail's word for the Cards destination; naming two
 * things on one screen with one word is what that choice avoids (Alex, 27 Aug 2026).
 */
export function countsLabel(rows: ResultRow[]): string {
  const counts = countByKind(rows);
  return [
    // **語彙 is always drawn, zero included**, which is what makes the paragraph above true: the
    // omit-a-zero rule is about the kinds that are *additions* to a dictionary search. A guard of
    // `vocab > 0 || grammar === 0` hides the count in exactly the case named as the finding —
    // 語彙0件 beside 文法2件 — while still printing 語彙0件 for a query nothing answered.
    `語彙${counts.vocab}件`,
    counts.grammar > 0 ? `文法${counts.grammar}件` : null,
    counts.name > 0 ? `名前${counts.name}件` : null,
  ]
    .filter(Boolean)
    .join(" · ");
}
